import { addDisplayName, addHandlers, addProps, flowMax } from 'ad-hok'
import cx from 'classnames'
import { FC } from 'react'
import { createUseStyles } from 'react-jss'
import { addApi } from 'shared/src/api'
import { addNavigate } from 'shared/src/utils/addNavigate'
import { trackEvent } from 'shared/src/utils/analytics'
import { addAuthContext } from 'shared/src/utils/auth'
import {
  addDataLoadingFromPromise,
  hasData,
} from 'shared/src/utils/dataLoading'

import { JoinWaitlistOrLaunchCmsButton } from 'components/JoinWaitlistOrLaunchCmsButton'
import { TermsOfService } from 'components/TermsOfService'
import { LogInOut } from 'components/menu/LogInOut'
import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { serifFontFamily } from 'utils/styles'
import {
  CONTACT_URL,
  INSTAGRAM_URL,
  SHOPPER_URL,
  TIK_TOK_URL,
  WAITLIST_URL,
} from 'utils/urls'
import instagramIcon from '../../assets/follow-us-instagram.svg'
import shareIcon from '../../assets/follow-us-share.svg'
import tikTokIcon from '../../assets/follow-us-tiktok.svg'
import { Button } from '../Button'
import { Link } from '../Link'
import { writeToClipboard } from '../feed/ShareButton'
import { addNotificationContext } from '../notifications'
import { Row } from './Row'

const useStyles = createUseStyles({
  container: {
    position: 'relative',
    paddingLeft: 40,
    paddingRight: 40,
  },
  table: {
    marginBottom: 19,

    '& hr': {
      height: 2,
      border: 'none',
      backgroundColor: 'rgba(255, 255, 255, 0.06)',
    },
  },
  followContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 20,
  },
  followText: {
    flexGrow: 1,
    fontFamily: serifFontFamily,
    fontSize: 22,
    fontWeight: 600,
    color: 'rgba(255, 255, 255, 0.88)',
  },
  followIcon: {
    height: 48,
  },
  joinWaitlistOrLaunchCmsButton: {
    marginBottom: 16,
  },
  logInOut: {
    marginBottom: 10,
  },
})

type Props = {
  className?: string
}

export const Footer: FC<Props> = flowMax(
  addDisplayName('Footer'),
  addApi,
  addAuthContext,
  addDataLoadingFromPromise(
    'user',
    ({ api, isAuthenticated }) =>
      isAuthenticated ? api.users.getCurrent() : Promise.resolve(undefined),

    [],
    () => 'footer'
  ),
  addProps(({ user }) => ({
    user: hasData(user) ? user.data : undefined,
  })),
  addNavigate,
  addNotificationContext,
  addTranslationHelpers,
  addHandlers({
    onShare:
      ({ showNotification, t }) =>
      () => {
        const sendEvent = () => {
          trackEvent(
            {
              category: 'general',
              action: 'share',
            },
            {
              url: SHOPPER_URL,
            }
          )
        }

        writeToClipboard(SHOPPER_URL)
          .then(() => {
            showNotification({ text: t('shareAlert'), style: 'top' })
            sendEvent()
          })
          .catch()
      },
  }),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({ onShare, user, classes, className, t }) => (
    <div className={cx(classes.container, className)}>
      <div className={classes.table}>
        <Row text={t('footer.discover')} url="/" />
        <hr />
        <Row text={t('footer.apply')} url={WAITLIST_URL} />
        <hr />
        <Row text={t('footer.contact')} url={CONTACT_URL} />
      </div>
      <div className={classes.followContainer}>
        <div className={classes.followText}>{t('footer.follow')}</div>
        <Link url={INSTAGRAM_URL} newTab>
          <img
            src={instagramIcon}
            alt={t('menu.followUs.instagramAlt')}
            className={classes.followIcon}
          />
        </Link>
        <Link url={TIK_TOK_URL} newTab>
          <img
            src={tikTokIcon}
            alt={t('menu.followUs.tikTokAlt')}
            className={classes.followIcon}
          />
        </Link>
        <Button onClick={onShare}>
          <img
            src={shareIcon}
            alt={t('menu.followUs.shareAlt')}
            className={classes.followIcon}
          />
        </Button>
      </div>
      <JoinWaitlistOrLaunchCmsButton
        user={user}
        className={classes.joinWaitlistOrLaunchCmsButton}
      />
      <LogInOut className={classes.logInOut} />
      <TermsOfService />
    </div>
  )
)
